// Helper compartilhado entre os portais (student-new, manager-new, teacher-new,
// company-new) pra garantir que o contexto ativo da sessão bate com o portal
// em que o usuário entrou.
//
// Por que existe: o servidor guarda UM contexto ativo por sessão. Se o
// usuário trocou pra Admin (manager), o cookie diz `ctx=admin`. Aí, se
// ele abrir /student/ manualmente (ou refresh em outra aba), o
// endpoint do portal retorna 403 — "contexto errado".
//
// Antes essa situação caía no /auth/, que via `activeContext=admin` e
// jogava o usuário em /manager/ — mesmo que ele claramente quisesse ficar
// no /student/. Agora, o portal:
//   1. Pede /auth/me
//   2. Se o `activeContext.role` não é o esperado, procura nos `contexts`
//      do usuário um que CASE com o que o portal espera
//   3. Se achou, chama /auth/switch-context e segue o bootstrap normal
//   4. Se não achou, aí sim manda pra /auth/ pra escolher

export interface AvailableContext {
  kind: "platform" | "company";
  companyId?: string;
  companyName?: string;
  role: string;
}

export interface PortalExpectedContext {
  /** "platform" pra manager (admin global), "company" pros demais. */
  kind: "platform" | "company";
  /** Role aceita. Aceita múltiplas pra portais flexíveis (ex.: admin_company). */
  roles: string[];
}

export interface MeShape {
  activeContext: AvailableContext | null;
  contexts: AvailableContext[];
}

/** Verifica se um contexto bate com o esperado pelo portal. */
export function contextMatches(
  ctx: AvailableContext | null | undefined,
  expected: PortalExpectedContext
): boolean {
  if (!ctx) return false;
  if (ctx.kind !== expected.kind) return false;
  if (!expected.roles.includes(ctx.role)) return false;
  return true;
}

/** Acha o melhor contexto entre os disponíveis pra esse portal. */
export function findMatchingContext(
  contexts: AvailableContext[],
  expected: PortalExpectedContext
): AvailableContext | null {
  for (const ctx of contexts) {
    if (contextMatches(ctx, expected)) return ctx;
  }
  return null;
}

/**
 * Garante o contexto certo antes do portal continuar.
 * Retorna `{ ok: true }` quando a sessão já está no contexto esperado
 * (após eventual auto-switch). Retorna `{ ok: false, reason }` quando
 * não tem como — caller deve redirecionar pro picker.
 */
export async function ensurePortalContext(
  expected: PortalExpectedContext,
  authPath = "/auth/"
): Promise<{ ok: boolean; reason?: "no_match" | "fetch_failed" | "switch_failed" }> {
  // /api/auth/me
  let me: MeShape;
  try {
    const res = await fetch("/api/auth/me", { credentials: "include" });
    if (!res.ok) {
      // 401 = sem sessão → vai pro login
      window.location.href = authPath;
      return { ok: false, reason: "fetch_failed" };
    }
    me = (await res.json()) as MeShape;
  } catch {
    window.location.href = authPath;
    return { ok: false, reason: "fetch_failed" };
  }

  // Já está no contexto certo? Segue.
  if (contextMatches(me.activeContext, expected)) {
    return { ok: true };
  }

  // Tem um contexto compatível na lista? Switch + segue.
  const candidate = findMatchingContext(me.contexts, expected);
  if (candidate) {
    try {
      const res = await fetch("/api/auth/switch-context", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          kind: candidate.kind,
          companyId: candidate.companyId,
          role: candidate.role
        })
      });
      if (!res.ok) {
        window.location.href = authPath;
        return { ok: false, reason: "switch_failed" };
      }
      return { ok: true };
    } catch {
      window.location.href = authPath;
      return { ok: false, reason: "switch_failed" };
    }
  }

  // Sem contexto compatível → manda pro picker.
  window.location.href = authPath;
  return { ok: false, reason: "no_match" };
}
